/** Матрица «оффер × регион» «сейчас»: строка — оффер (фото, артикул, nm, название WB / 1С,
 *  сопоставление), столбцы — макрорегионы + «Зарубеж» + «Не опр.», ячейка — дни FBO+FBS
 *  (`cover_days_total` строки витрины), цвет — ТОЛЬКО `deficit_class` витрины; под числом мелко
 *  FBO / FBS / спрос. «Зарубеж» из нескольких стран — без цвета, страны в подсказке. Справа —
 *  итог оффера (бэкенд): FBO «надёжно · слабо». FBO — оценка, не факт: уверенность и слабая часть
 *  (weak + unresolved) — в подсказке ячейки, слабая уверенность (< 0,8) — пометкой «≈» у FBO.
 *  Фильтры — в адресе (state.ts). Строки и ячейки — `React.memo`: ввод в поиск и наведение не
 *  перерисовывают всю матрицу. */
import { PanelRight } from "lucide-react";
import React, { memo, useMemo } from "react";

import type { WbOffer, WbOfferCell, WbOffers, WbRegionRef } from "@/api/wbRegions";
import { mpProductUrl } from "@/lib/marketplace";
import { cn } from "@/lib/utils";
import { thClass, tdNum } from "../ozon-analytics/ui";
import { IncompleteMark, OfferPhoto, PerDay, Qty } from "./cells";
import { useDebouncedText } from "./useDebouncedText";
import { daysText, dec1, deficitClassName, deficitLabel, DEFICIT_STYLE, isWeakConfidence, qtyText } from "./format";
import { pct } from "../ozon-analytics/format";
import { buildColumns, cellsByColumn, cellTitle, filterOffers, sortOffers, sum2, type Column } from "./matrix";
import { ABROAD, RESET_FILTERS, type CardRef, type Patch } from "./state";

function SubLine({ c }: { c: WbOfferCell }): React.ReactElement {
  const weak = isWeakConfidence(c.confidence);
  return (
    <div className="text-[10px] leading-3 opacity-80">
      {weak && (
        <span className="italic" data-testid="weak-fbo" aria-label="слабая уверенность FBO-оценки">
          ≈
        </span>
      )}
      <span className={cn(weak && "italic")}>
        <Qty v={c.fbo_stock} />
      </span>{" "}
      / <Qty v={c.fbs_stock} /> / <PerDay v={c.orders_28d} />
    </div>
  );
}

const MatrixCell = memo(function MatrixCell({
  cells,
  column,
  focused,
}: {
  cells: WbOfferCell[];
  column: Column;
  focused: boolean;
}): React.ReactElement {
  const base = cn("px-1.5 py-1 text-right tabular-nums whitespace-nowrap", focused && "ring-1 ring-inset ring-primary/40");
  if (cells.length === 0) {
    return (
      <td className={cn(base, "text-muted-foreground/60")} title={`${column.title}: строки нет — ни остатка, ни спроса`} data-testid="cell-empty">
        —
      </td>
    );
  }
  if (cells.length > 1) {
    // несколько стран: класс группы не вычисляем — без цвета, по странам в подсказке
    const counts = new Map<string, number>();
    for (const c of cells) {
      const l = deficitLabel(c.deficit_class);
      counts.set(l, (counts.get(l) ?? 0) + 1);
    }
    return (
      <td className={cn(base, "cursor-help")} title={cells.map(cellTitle).join("\n\n")} data-testid="cell-countries">
        <div className="leading-4">{cells.length} стран</div>
        <div className="text-[10px] leading-3 text-muted-foreground">
          {[...counts].map(([l, n]) => `${l} ${n}`).join(" · ")}
        </div>
      </td>
    );
  }
  const c = cells[0]!;
  return (
    <td
      className={cn(base, "cursor-help", deficitClassName(c.deficit_class))}
      title={cellTitle(c)}
      data-testid="cell"
      data-class={c.deficit_class ?? "no_demand"}
    >
      <div className="leading-4">
        {c.cover_days_total !== null ? dec1(c.cover_days_total) : daysText(null, c.orders_28d)}
        <IncompleteMark orders={c.orders_complete} sales={c.sales_complete} />
      </div>
      <SubLine c={c} />
    </td>
  );
});

function OfferHead({
  o,
  showLegal,
  onOpenCard,
}: {
  o: WbOffer;
  showLegal: boolean;
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  const url = mpProductUrl("wb", o.mp_sku);
  const title = o.name ?? o.name_1c;
  return (
    <div className="flex min-w-[260px] items-start gap-2">
      <OfferPhoto url={o.image_url} alt={title ?? o.vendor_code ?? o.mp_sku ?? "оффер"} />
      <div className="min-w-0 space-y-0.5">
        <div className="flex items-center gap-1.5">
          {o.offer_key ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 font-medium hover:underline"
              title="Открыть карточку оффера"
              onClick={() => onOpenCard({ kind: "offer", legal: o.legal_name, offerKey: o.offer_key! })}
            >
              {o.vendor_code ?? <span className="text-muted-foreground">без артикула</span>}
              <PanelRight className="size-3 text-muted-foreground" aria-hidden />
            </button>
          ) : (
            <span className="font-medium" title="offer_key не определён (размер) — карточки нет">
              {o.vendor_code ?? "без артикула"}
            </span>
          )}
          {url ? (
            <a href={url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:underline">
              nm {o.mp_sku}
            </a>
          ) : (
            <span className="text-muted-foreground">nm нет</span>
          )}
          {showLegal && <span className="rounded border px-1 text-[10px] text-muted-foreground">{o.legal_name}</span>}
        </div>
        <div className="max-w-[320px] truncate text-muted-foreground" title={[o.name, o.name_1c].filter(Boolean).join(" / ")}>
          {o.name ?? <span className="italic">нет названия WB</span>}
          {o.name_1c && <> / {o.name_1c}</>}
        </div>
        <div className="flex flex-wrap gap-1 text-[10px]">
          {o.mapping_status !== "ok" && (
            <span className="rounded bg-amber-100 px-1 text-amber-900 dark:bg-amber-950 dark:text-amber-300" title="Сопоставление с 1С">
              1С: {o.mapping_status ?? "нет статуса"}
            </span>
          )}
          {o.dim_missing && (
            <span className="rounded bg-muted px-1 text-muted-foreground" data-testid="dim-missing">
              {o.dim_label ?? "нет в mp_finance_sku_dim"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/** FBO оффера по всем регионам: «надёжно · слабо / не опр.» (оценка, не факт). */
function OfferFbo({ o }: { o: WbOffer }): React.ReactElement {
  const t = o.total;
  const weak = sum2(t.fbo_stock_weak, t.fbo_stock_unresolved);
  return (
    <span
      className="cursor-help"
      title={`FBO ${qtyText(t.fbo_stock)} (оценка), уверенность ${t.confidence === null ? "—" : pct(t.confidence)}`}
      data-testid="offer-fbo"
    >
      <Qty v={sum2(t.fbo_stock_known_chain, t.fbo_stock_by_supply_signal)} />
      <span className="text-muted-foreground">
        {" "}
        · <Qty v={weak} />
      </span>
    </span>
  );
}

const OfferRow = memo(function OfferRow({
  o,
  columns,
  focus,
  showLegal,
  onOpenCard,
}: {
  o: WbOffer;
  columns: Column[];
  focus: string | null;
  showLegal: boolean;
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  const byCol = useMemo(() => cellsByColumn(o), [o]);
  return (
    <tr className="border-t align-top" data-testid="offer-row">
      <td className="px-2 py-1">
        <OfferHead o={o} showLegal={showLegal} onOpenCard={onOpenCard} />
      </td>
      {columns.map((c) => (
        <MatrixCell key={c.key} cells={byCol.get(c.key) ?? EMPTY} column={c} focused={c.key === focus} />
      ))}
      <td className={cn(tdNum, "border-l")}>
        <PerDay v={o.total.orders_28d} />
      </td>
      <td className={tdNum}>
        <OfferFbo o={o} />
      </td>
      <td className={tdNum}>
        <Qty v={o.total.fbs_stock} />
      </td>
      <td className={cn(tdNum, "text-muted-foreground")} data-testid="offer-frozen">
        <Qty v={o.total.frozen_stock} /> · <Qty v={o.total.vanished_stock} />
      </td>
    </tr>
  );
});

const EMPTY: WbOfferCell[] = [];

/** Поиск с задержкой: в адрес (`q`) уходит через 400 мс после ввода. */
function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }): React.ReactElement {
  const [draft, setDraft] = useDebouncedText(value, onChange);
  return (
    <input
      type="search"
      aria-label="Поиск оффера"
      placeholder="Артикул, nm, название"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      className="h-8 w-64 rounded-md border bg-background px-2 text-xs"
    />
  );
}

export function OfferMatrix({
  data,
  metaRegions,
  q,
  deficitOnly,
  hideNoDemand,
  focus,
  searching,
  showLegal,
  onPatch,
  onOpenCard,
}: {
  data: WbOffers;
  metaRegions: WbRegionRef[];
  q: string;
  deficitOnly: boolean;
  hideNoDemand: boolean;
  focus: string | null;
  /** идёт новый поиск — на экране прежний ответ */
  searching: boolean;
  showLegal: boolean;
  onPatch: (p: Patch) => void;
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  const columns = useMemo(() => buildColumns(metaRegions, data.offers), [metaRegions, data.offers]);
  const shown = useMemo(
    () => sortOffers(filterOffers(data.offers, { deficitOnly, hideNoDemand }), focus),
    [data.offers, deficitOnly, hideNoDemand, focus],
  );
  const focusCol = columns.find((c) => c.key === focus);
  const filtered = Boolean(q || deficitOnly || hideNoDemand);

  return (
    <div className="space-y-2" data-testid="offer-matrix">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <SearchBox value={q} onChange={(v) => onPatch({ q: v || null })} />
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={deficitOnly}
            onChange={(e) => onPatch({ def: e.target.checked ? "1" : null })}
          />
          Только с дефицитом
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={hideNoDemand}
            onChange={(e) => onPatch({ nd: e.target.checked ? "1" : null })}
          />
          Скрыть без спроса
        </label>
        {focusCol && (
          <span className="rounded-full border px-2 py-0.5" data-testid="focus-chip">
            Сортировка по региону «{focusCol.title}»
            {focus === ABROAD ? " (по спросу стран)" : " (дни FBO+FBS, меньше — выше)"}{" "}
            <button type="button" className="ml-1 text-muted-foreground hover:underline" onClick={() => onPatch({ focus: null })}>
              ✕
            </button>
          </span>
        )}
        {searching && <span className="text-muted-foreground">ищу…</span>}
        <span className="text-muted-foreground" data-testid="offers-count">
          офферов: {shown.length} из {data.offers.length}
        </span>
      </div>
      <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
        Цвет ячейки — класс дефицита витрины по дням FBO+FBS:
        {(["zero", "low", "ok", "excess"] as const).map((k) => (
          <span key={k} className={cn("rounded px-1", DEFICIT_STYLE[k].cls, k === "ok" && "border")}>
            {DEFICIT_STYLE[k].label}
          </span>
        ))}
        <span className="rounded border px-1">нет спроса — серым</span>
        <span>· под числом: FBO / FBS / спрос в день; «≈» — слабая уверенность FBO-оценки (&lt; 80 %)</span>
      </div>

      {shown.length === 0 ? (
        <div className="rounded-md border bg-muted/30 px-4 py-6 text-sm" data-testid="matrix-empty">
          {filtered ? (
            <>
              Ничего не найдено.{" "}
              <button type="button" className="underline" onClick={() => onPatch(RESET_FILTERS)}>
                Сбросить фильтры
              </button>
            </>
          ) : (
            "У кабинета нет строк в витрине «сейчас»."
          )}
        </div>
      ) : (
        <div className="max-h-[70vh] overflow-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-muted">
              <tr>
                <th className={cn(thClass, "text-left")}>Оффер</th>
                {columns.map((c) => (
                  <th key={c.key} className={thClass} aria-sort={c.key === focus ? "ascending" : undefined}>
                    <button
                      type="button"
                      title={`${c.title}: сортировать по региону${c.key === focus ? " (повторно — снять)" : ""}`}
                      aria-pressed={c.key === focus}
                      className={cn("hover:text-foreground hover:underline", c.key === focus && "text-foreground underline")}
                      onClick={() => onPatch({ focus: c.key === focus ? null : c.key })}
                      data-testid="column-sort"
                    >
                      {c.label}
                    </button>
                  </th>
                ))}
                <th className={cn(thClass, "border-l")} title="Заказов в день за 28 дней, все регионы">Спрос/день</th>
                <th className={thClass} title="FBO-оценка: надёжно (цепочка, сигналы поставки) · слабо / склад не определён">
                  FBO, шт.: надёжно · слабо
                </th>
                <th className={thClass}>FBS, шт.</th>
                <th className={thClass} title="Справочно: заморожено · исчезнувший склад; не входит в FBO и дни">
                  Заморожено
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((o) => (
                <OfferRow
                  key={`${o.legal_name}:${o.mp_sku}:${o.mp_size}`}
                  o={o}
                  columns={columns}
                  focus={focus}
                  showLegal={showLegal}
                  onOpenCard={onOpenCard}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
